import { addDays, addMonths } from '@fd/finance-core';
import type { Route } from './router.tsx';

// What needs the owner's eye, gathered from the local data on every device (no server job): a
// budget used up, a regular payment that did not come or came higher, a large expense, last month
// still to settle, a bank to renew. Pure: the screen passes in what it has, the list comes out.

type NotificationTone = 'bad' | 'warn' | 'info';

type Base = {
  /** Stable identity: read and hidden marks follow it (a new month, a new due day → a new key). */
  readonly key: string;
  readonly tone: NotificationTone;
  /** When it became true, for the order (newest first). */
  readonly date: string;
  readonly route: Route;
};

export type AppNotification = Base &
  (
    | { readonly kind: 'budgetOver' | 'budgetNear'; readonly name: string | null; readonly spentMinor: bigint; readonly limitMinor: bigint; readonly currency: string }
    | { readonly kind: 'paymentLate' | 'incomeLate'; readonly name: string; readonly dueDate: string }
    | { readonly kind: 'paymentHigher'; readonly name: string; readonly amountMinor: bigint; readonly usualMinor: bigint; readonly currency: string }
    | { readonly kind: 'largeExpense'; readonly name: string; readonly amountMinor: bigint; readonly currency: string }
    | { readonly kind: 'settleUp' | 'settled'; readonly month: string; readonly fromPersonId: string; readonly toPersonId: string; readonly amountMinor: bigint; readonly currency: string }
    | { readonly kind: 'bankRenew'; readonly bank: string; readonly until: string }
    | { readonly kind: 'bankReconnect'; readonly bank: string }
  );

export type NotificationSettings = {
  readonly budgets: boolean;
  readonly payments: boolean;
  readonly large: boolean;
  /** A single expense from this much (base currency) is "large". */
  readonly largeFromMinor: bigint;
  readonly settlement: boolean;
  readonly banks: boolean;
  /** Also as the system's own notifications while the app is open (the browser asks first). */
  readonly system: boolean;
};

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = { budgets: true, payments: true, large: true, largeFromMinor: 20_000n, settlement: true, banks: true, system: false };

/** Budgets: at 90% a heads-up, over the limit a warning. */
const NEAR_BASIS_POINTS = 9_000;
/** A regular payment 20% (and at least 5.00) above its usual amount. */
const HIGHER_PERCENT = 120n;
const HIGHER_AT_LEAST_MINOR = 500n;
/** How long a fresh event stays in the list. */
const RECENT_DAYS = 10;
const LARGE_DAYS = 7;
/** Last month is offered for settling during the first days of the next. */
const SETTLE_UNTIL_DAY = 10;

export type BudgetInput = {
  readonly key: string;
  /** Null: all spending. */
  readonly name: string | null;
  readonly month: string;
  readonly spentMinor: bigint;
  readonly limitMinor: bigint;
  readonly usedBasisPoints: number;
  readonly currency: string;
};

export type RecurringInput = {
  readonly signature: string;
  readonly name: string;
  readonly status: 'SUGGESTED' | 'CONFIRMED' | 'DISMISSED';
  readonly active: boolean;
  readonly overdue: boolean;
  readonly direction: 'IN' | 'OUT';
  readonly kind: 'SUBSCRIPTION' | 'BILL' | 'DEBT' | 'INCOME' | 'TRANSFER' | 'OTHER';
  readonly nextDate: string;
  readonly lastDate: string;
  readonly lastAmountMinor: bigint;
  readonly typicalAmountMinor: bigint;
  readonly fixedAmount: boolean;
  readonly priceChange: { readonly previousMinor: bigint; readonly currentMinor: bigint; readonly since: string } | null;
  readonly currency: string;
  readonly transactionIds: readonly string[];
};

export type ExpenseInput = {
  readonly id: string;
  readonly name: string;
  readonly bookingDate: string;
  /** Positive, in the base currency. */
  readonly amountMinor: bigint;
};

export type SettlementInput = {
  /** First day of the month the transfers settle. */
  readonly month: string;
  readonly currency: string;
  readonly transfers: readonly { readonly fromPersonId: string; readonly toPersonId: string; readonly amountMinor: bigint }[];
};

/** A settlement recorded by itself when the transfer came in (auto-settle.ts). */
export type SettledInput = {
  readonly id: string;
  readonly settledOn: string;
  /** First day of the month it settled. */
  readonly month: string;
  readonly currency: string;
  readonly transfers: readonly { readonly fromPersonId: string; readonly toPersonId: string; readonly amountMinor: bigint }[];
};

export type BankInput = {
  readonly id: string;
  readonly bank: string;
  readonly status: string;
  readonly reauthorizationRequired: boolean;
  readonly consentExpiresAt: string | null;
};

export type NotificationInput = {
  readonly today: string;
  readonly settings: NotificationSettings;
  readonly baseCurrency: string;
  readonly budgets: readonly BudgetInput[];
  readonly recurring: readonly RecurringInput[];
  readonly expenses: readonly ExpenseInput[];
  readonly settlement: SettlementInput | null;
  /** Settled by themselves lately (optional: older callers). */
  readonly settled?: readonly SettledInput[];
  readonly banks: readonly BankInput[];
};

const REGULAR = new Set<RecurringInput['kind']>(['SUBSCRIPTION', 'BILL', 'DEBT', 'INCOME']);

export function collectNotifications(input: NotificationInput): AppNotification[] {
  const { today, settings } = input;
  const out: AppNotification[] = [];

  if (settings.budgets) {
    for (const b of input.budgets) {
      if (b.limitMinor <= 0n) continue;
      const over = b.spentMinor > b.limitMinor;
      // Exactly the limit (the rent on a housing budget) is the plan, not news.
      if (!over && (b.usedBasisPoints < NEAR_BASIS_POINTS || b.spentMinor === b.limitMinor)) continue;
      out.push({
        key: `budget:${b.key}:${b.month}:${over ? 'over' : 'near'}`,
        kind: over ? 'budgetOver' : 'budgetNear',
        tone: over ? 'bad' : 'warn',
        date: today,
        route: { page: 'budgets' },
        name: b.name,
        spentMinor: b.spentMinor,
        limitMinor: b.limitMinor,
        currency: b.currency,
      });
    }
  }

  const regular = input.recurring.filter((r) => r.status !== 'DISMISSED' && r.active && REGULAR.has(r.kind));
  if (settings.payments) {
    for (const r of regular) {
      if (r.overdue && r.nextDate < today) {
        out.push({
          key: `late:${r.signature}:${r.nextDate}`,
          kind: r.direction === 'IN' ? 'incomeLate' : 'paymentLate',
          tone: 'warn',
          date: r.nextDate,
          route: { page: 'recurring' },
          name: r.name,
          dueDate: r.nextDate,
        });
      }
      if (r.direction !== 'OUT' || r.lastDate < addDays(today, -RECENT_DAYS)) continue;
      // A new price since the last payment, or one payment well above the usual.
      const change = r.priceChange && r.priceChange.since >= addDays(today, -RECENT_DAYS) && r.priceChange.currentMinor > r.priceChange.previousMinor ? r.priceChange : null;
      const usual = change ? change.previousMinor : r.typicalAmountMinor;
      const higher = change !== null || (!r.fixedAmount && r.lastAmountMinor * 100n > usual * HIGHER_PERCENT && r.lastAmountMinor - usual >= HIGHER_AT_LEAST_MINOR);
      if (!higher) continue;
      out.push({
        key: `higher:${r.signature}:${r.lastDate}`,
        kind: 'paymentHigher',
        tone: 'warn',
        date: r.lastDate,
        route: { page: 'recurring' },
        name: r.name,
        amountMinor: change ? change.currentMinor : r.lastAmountMinor,
        usualMinor: usual,
        currency: r.currency,
      });
    }
  }

  if (settings.large && settings.largeFromMinor > 0n) {
    // A regular payment (rent) is expected, not news.
    const expected = new Set(regular.flatMap((r) => r.transactionIds));
    for (const e of input.expenses) {
      if (e.bookingDate < addDays(today, -LARGE_DAYS) || e.bookingDate > today || e.amountMinor < settings.largeFromMinor || expected.has(e.id)) continue;
      out.push({
        key: `large:${e.id}`,
        kind: 'largeExpense',
        tone: 'info',
        date: e.bookingDate,
        route: { page: 'transactions', id: e.id },
        name: e.name,
        amountMinor: e.amountMinor,
        currency: input.baseCurrency,
      });
    }
  }

  if (settings.settlement && input.settlement && Number(today.slice(8, 10)) <= SETTLE_UNTIL_DAY && input.settlement.month === addMonths(`${today.slice(0, 8)}01`, -1)) {
    for (const transfer of input.settlement.transfers) {
      if (transfer.amountMinor <= 0n) continue;
      out.push({
        key: `settle:${input.settlement.month}:${input.settlement.currency}:${transfer.fromPersonId}:${transfer.toPersonId}`,
        kind: 'settleUp',
        tone: 'info',
        date: `${today.slice(0, 8)}01`,
        route: { page: 'settlements' },
        month: input.settlement.month,
        fromPersonId: transfer.fromPersonId,
        toPersonId: transfer.toPersonId,
        amountMinor: transfer.amountMinor,
        currency: input.settlement.currency,
      });
    }
  }

  // A month that settled by itself when the transfer came in: said once, for the next ten days.
  if (settings.settlement) {
    for (const s of input.settled ?? []) {
      if (s.settledOn < addDays(today, -RECENT_DAYS) || s.settledOn > today) continue;
      for (const transfer of s.transfers) {
        out.push({
          key: `settled:${s.id}:${transfer.fromPersonId}:${transfer.toPersonId}`,
          kind: 'settled',
          tone: 'info',
          date: s.settledOn,
          route: { page: 'settlements' },
          month: s.month,
          fromPersonId: transfer.fromPersonId,
          toPersonId: transfer.toPersonId,
          amountMinor: transfer.amountMinor,
          currency: s.currency,
        });
      }
    }
  }

  if (settings.banks) {
    for (const b of input.banks) {
      if (b.reauthorizationRequired || b.status === 'ACTION_REQUIRED' || b.status === 'CONSENT_EXPIRED' || b.status === 'EXPIRED') {
        out.push({ key: `bank:${b.id}:reconnect:${b.consentExpiresAt ?? ''}`, kind: 'bankReconnect', tone: 'bad', date: today, route: { page: 'accounts' }, bank: b.bank });
      } else if (b.status === 'CONSENT_EXPIRING' && b.consentExpiresAt) {
        out.push({ key: `bank:${b.id}:renew:${b.consentExpiresAt.slice(0, 10)}`, kind: 'bankRenew', tone: 'warn', date: today, route: { page: 'accounts' }, bank: b.bank, until: b.consentExpiresAt.slice(0, 10) });
      }
    }
  }

  const weight: Record<NotificationTone, number> = { bad: 0, warn: 1, info: 2 };
  return out.sort((a, b) => (a.date !== b.date ? (a.date < b.date ? 1 : -1) : weight[a.tone] - weight[b.tone]));
}
